import React from "react";
import { Link } from "react-router-dom";
import { TruckIcon } from "@heroicons/react/24/outline";
import { useGetEquipmentComplianceSummaryQuery } from "../../redux/services/equipment";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";

/**
 * Trucking dashboard widget — fleet compliance + PM + availability counts.
 */
const FleetComplianceCard = () => {
  const { hasPermission } = usePermissions();
  const canReadEquipment = hasPermission(PERMISSIONS.EQUIPMENT_READ);
  const { data, isLoading, isError } = useGetEquipmentComplianceSummaryQuery(
    undefined,
    { skip: !canReadEquipment }
  );

  if (!canReadEquipment) {
    return null;
  }

  const complianceRows = [
    { key: "registrationExpired", label: "Registration expired" },
    { key: "insuranceExpired", label: "Insurance expired" },
    { key: "inspectionOverdue", label: "Inspection overdue" },
    { key: "inspectionDueSoon", label: "Inspection due soon" },
  ];

  const pmRows = [
    { key: "pmOverdue", label: "PM overdue" },
    { key: "pmDueSoon", label: "PM due soon" },
  ];

  const availabilityRows = [
    { key: "available", label: "Available units" },
    { key: "unavailable", label: "Unavailable units" },
    { key: "inMaintenance", label: "In maintenance" },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TruckIcon className="h-5 w-5 text-primary-600" />
          <h3 className="text-sm font-semibold text-gray-900">Fleet health</h3>
        </div>
        <Link
          to="/equipment"
          className="text-xs font-medium text-primary-600 hover:text-primary-800"
        >
          View fleet
        </Link>
      </div>

      {isLoading && (
        <p className="text-sm text-gray-500">Loading fleet summary…</p>
      )}
      {isError && (
        <p className="text-sm text-gray-500">
          Fleet summary is unavailable in this environment.
        </p>
      )}
      {!isLoading && !isError && data && (
        <>
          <p className="mb-3 text-sm text-gray-600">
            {data.withAnyAlert > 0 || data.pmOverdue > 0
              ? `${data.total} units — attention needed`
              : `${data.total} units — all clear`}
          </p>
          <ul className="space-y-1 text-sm">
            {availabilityRows.map((row) => (
              <li key={row.key} className="flex justify-between">
                <span className="text-gray-700">{row.label}</span>
                <span className="font-medium text-gray-800">{data[row.key] || 0}</span>
              </li>
            ))}
            {complianceRows.map((row) => (
              <li key={row.key} className="flex justify-between">
                <span className="text-gray-700">{row.label}</span>
                <Link
                  to={`/equipment?alert=${row.key}`}
                  className={`font-medium ${
                    data[row.key] > 0 ? "text-red-600" : "text-gray-400"
                  }`}
                >
                  {data[row.key]}
                </Link>
              </li>
            ))}
            {pmRows.map((row) => (
              <li key={row.key} className="flex justify-between">
                <span className="text-gray-700">{row.label}</span>
                <Link
                  to={`/equipment?pm=${row.key}`}
                  className={`font-medium ${
                    data[row.key] > 0 ? "text-amber-600" : "text-gray-400"
                  }`}
                >
                  {data[row.key]}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default FleetComplianceCard;
